import { computed, ref, toValue, type MaybeRefOrGetter } from "vue";

/**
 * Whether the server refused a project or a dataspace for what a license takes away from it — and
 * which one — the way `useLicenseGate` knows the server refused the Designer or the Operator for
 * want of a license, but scoped to the space the refusal concerns.
 *
 * Set by the infra that sees the refusal, with nothing imported from it so the infra can set it
 * without a cycle:
 *
 *  - a `403 designer_unavailable` or `403 space_locked` from any API call (`@/api`), for the space
 *    the request's path names — `/projects/{id}/…`, `/dataspaces/{id}/…`. A create has no space to
 *    name, so it is not recorded; its dialog words the refusal by code.
 *  - a room join acked `{ ok: false, code: "designer_unavailable" | "space_locked", reason }`
 *    (`@/socket`), for the space the room is.
 *
 * Read by the layouts, which show `SpaceRestrictedState` instead of the page. What the page shows
 * normally comes from the space itself (`ProjectResponse.restriction`, `DataspaceResponse.restriction`);
 * this is the REFUSAL, which arrives first (a join is acked before the space is read) and which a
 * session that read the space a moment too early has not heard of yet. So a refusal is also the
 * layouts' cue to read the space again.
 *
 * It is cleared three ways, and a refusal never outlives what it was about:
 *  - a fresh read of the space that no longer says it (`reconcileSpaceRefusal`, from the stores that
 *    read it);
 *  - a room join the server accepted;
 *  - entering a section, or a space, asks again (`clearSpaceRefusal`, `clearSpaceRefusals`), and so
 *    does "Try again".
 */

export type RefusedSpaceKind = "project" | "dataspace";
export type SpaceRefusalCode = "designer_unavailable" | "space_locked";

export interface SpaceRefusal {
	kind: RefusedSpaceKind;
	id: string;
	code: SpaceRefusalCode;
	/** `server` / `department` for a closed Designer; a lock reason for a locked space. Empty when
	 *  the server did not say. */
	reason: string;
}

const refusals = ref<Record<string, SpaceRefusal>>({});

const keyOf = (kind: RefusedSpaceKind, id: string) => `${kind}:${id}`;

/**
 * The space a request path is about: `/projects/{id}/…` and `/dataspaces/{id}/…`. Null for any
 * other path — a list, a create, the control center's own routes, which no restriction refuses.
 */
export function spaceOfPath(path: string): { kind: RefusedSpaceKind; id: string } | null {
	const match = /^\/(projects|dataspaces)\/([^/?#]+)/.exec(path);
	if (!match) return null;
	return {
		kind: match[1] === "projects" ? "project" : "dataspace",
		id: decodeURIComponent(match[2]),
	};
}

/** The server refused something about this space for a restriction. */
export function markSpaceRefused(refusal: SpaceRefusal): void {
	const held = refusals.value[keyOf(refusal.kind, refusal.id)];
	// The same answer again (every request of a page is refused alike) changes nothing.
	if (held && held.code === refusal.code && held.reason === refusal.reason) return;
	refusals.value = { ...refusals.value, [keyOf(refusal.kind, refusal.id)]: refusal };
}

/** Forget what was refused for one space, so the next request decides again. */
export function clearSpaceRefusal(kind: RefusedSpaceKind, id: string): void {
	const key = keyOf(kind, id);
	if (!(key in refusals.value)) return;
	const { [key]: _gone, ...rest } = refusals.value;
	refusals.value = rest;
}

/** Forget every refusal — entering the Designer or the Operator asks again. */
export function clearSpaceRefusals(): void {
	if (Object.keys(refusals.value).length) refusals.value = {};
}

/**
 * A fresh read of the space says what is taken away from it NOW: a refusal it no longer backs up
 * is dropped. `designer_unavailable` goes when the Designer is open again, `space_locked` when the
 * space is unlocked; one the read still says is kept.
 */
export function reconcileSpaceRefusal(
	kind: RefusedSpaceKind,
	id: string,
	restriction: { designer?: unknown; locked?: unknown } | null | undefined,
): void {
	const held = refusals.value[keyOf(kind, id)];
	if (!held) return;
	const stillSo =
		held.code === "designer_unavailable" ? restriction?.designer : restriction?.locked;
	if (!stillSo) clearSpaceRefusal(kind, id);
}

/** What was refused for one space, if anything — for code that is not a component. */
export function spaceRefusalOf(kind: RefusedSpaceKind, id: string): SpaceRefusal | null {
	return refusals.value[keyOf(kind, id)] ?? null;
}

/**
 * The refusal, if any, for the space `id` names — following the id as it changes. `designer` and
 * `locked` are the two refusals separately, because a page reads the one that closes it: a project's
 * whole area is closed by `designer_unavailable`, while `space_locked` only says what the space
 * may do.
 */
export function useSpaceRestriction(
	kind: RefusedSpaceKind,
	id: MaybeRefOrGetter<string | null | undefined>,
) {
	const refusal = computed(() => {
		const space = toValue(id);
		return space ? (refusals.value[keyOf(kind, space)] ?? null) : null;
	});
	return {
		refusal,
		designer: computed(() =>
			refusal.value?.code === "designer_unavailable" ? refusal.value : null,
		),
		locked: computed(() => (refusal.value?.code === "space_locked" ? refusal.value : null)),
		clear: () => {
			const space = toValue(id);
			if (space) clearSpaceRefusal(kind, space);
		},
	};
}
